/**
 * FieldEngine — the session-level orchestrator that ties the pieces together.
 *
 * It holds the live field of fragments and the participant session, advances
 * vitality over time, decides what surfaces, records dwell, and produces
 * mutations (via an injected MutationAdapter). It has no knowledge of rendering,
 * cameras, networking, or React. The client drives it; the server can reuse it.
 */
import { fragmentState } from './model.js';
import { getRegime } from './regimes.js';
import { makeRng, seedFromString } from './rng.js';
import { decayedVitality, isDead, reinforce } from './vitality.js';
import { scoreCandidates, surfaceNext, dominantTactics } from './selection.js';
import { selectOperator, shouldMutate } from './mutation.js';
import { childFragment } from './factory.js';
/** Per-session cap on how many times one parent may be re-mutated. */
export const DEFAULT_MAX_MUTATIONS_PER_PARENT = 2;
/** Minimum gap between mutations of the same parent, ms. */
export const DEFAULT_MUTATION_COOLDOWN_MS = 20_000;
export class FieldEngine {
    session;
    pool;
    liveIds = new Set();
    regime;
    rng;
    /**
     * Per-parent mutation bookkeeping. `shouldMutate` stays true for as long as a
     * fragment has been dwelt on or reused, and a parent is never "used up", so
     * without this a single held fragment would be re-mutated every pass forever
     * (two LLM calls and two new pool entries each time).
     */
    mutations = new Map();
    constructor(opts) {
        this.session = opts.session;
        this.pool = new Map(opts.pool.map((f) => [f.id, f]));
        this.regime = getRegime(opts.regime ?? 'neutral');
        this.rng = makeRng(opts.seed ?? seedFromString(opts.session.id));
    }
    setRegime(name) {
        this.regime = getRegime(name);
    }
    getRegime() {
        return this.regime;
    }
    /** Add or replace a fragment in the pool (e.g. inherited from another session). */
    addToPool(f) {
        this.pool.set(f.id, f);
    }
    /** Whether a fragment with this id is already in the pool. */
    hasFragment(id) {
        return this.pool.has(id);
    }
    /**
     * Add a fragment and immediately surface it into the live field, bypassing
     * normal selection. Used for watcher observations that must appear at once.
     */
    injectSurfaced(f) {
        this.pool.set(f.id, f);
        this.liveIds.add(f.id);
        if (!this.session.seen.includes(f.id))
            this.session.seen.push(f.id);
    }
    live() {
        return [...this.liveIds].map((id) => this.pool.get(id)).filter(Boolean);
    }
    poolFragments() {
        return [...this.pool.values()];
    }
    snapshot() {
        return {
            live: this.live(),
            regime: this.regime.name,
            dominant: dominantTactics(this.session),
            sessionId: this.session.id,
        };
    }
    /**
     * Record that a fragment was held in frame for `seconds`. Reinforces vitality
     * and accrues tactic dwell for the drift map.
     */
    dwell(fragmentId, seconds, now = Date.now()) {
        const f = this.pool.get(fragmentId);
        if (!f || seconds <= 0)
            return;
        const updated = reinforce(f, now, this.regime, { dwellSeconds: seconds });
        this.pool.set(fragmentId, updated);
        this.session.tacticDwell[f.tactic] += seconds;
        if (!this.session.seen.includes(fragmentId))
            this.session.seen.push(fragmentId);
    }
    /** Mark a fragment as cultivated and fed forward (reuse reinforcement). */
    cultivate(fragmentId, now = Date.now()) {
        const f = this.pool.get(fragmentId);
        if (!f)
            return;
        const updated = reinforce(f, now, this.regime, { reuseEvents: 1 });
        this.pool.set(fragmentId, updated);
        if (!this.session.fed.includes(fragmentId))
            this.session.fed.push(fragmentId);
    }
    /**
     * Advance the field: decay all live fragments, retire dead ones, and surface
     * new fragments to refill toward the regime target. Returns the fragments that
     * were newly surfaced this tick.
     */
    tick(now = Date.now()) {
        const retired = [];
        // Decay + retire.
        for (const id of [...this.liveIds]) {
            const f = this.pool.get(id);
            if (!f) {
                this.liveIds.delete(id);
                continue;
            }
            const decayed = { ...f, vitality: decayedVitality(f, now, this.regime), vitalityUpdatedAt: now };
            this.pool.set(id, decayed);
            if (isDead(decayed)) {
                this.liveIds.delete(id);
                retired.push(id);
            }
        }
        // Refill.
        const candidates = this.poolFragments().filter((f) => !this.liveIds.has(f.id) && !isDead(f));
        const picks = surfaceNext(candidates, this.liveIds.size, this.session, this.regime, this.rng);
        const surfaced = [];
        for (const p of picks) {
            this.liveIds.add(p.fragment.id);
            const bumped = { ...p.fragment, seenCount: p.fragment.seenCount + 1 };
            this.pool.set(p.fragment.id, bumped);
            if (!this.session.seen.includes(p.fragment.id))
                this.session.seen.push(p.fragment.id);
            surfaced.push(bumped);
        }
        return { surfaced, retired };
    }
    /** The legible "why did this surface" list, for seams mode. */
    explain() {
        return scoreCandidates(this.live(), this.session, this.regime, this.rng).map((s) => ({
            id: s.fragment.id,
            reason: s.reason,
        }));
    }
    /**
     * Whether a fragment may be mutated again right now: under its per-parent cap
     * and past the cooldown since its last mutation attempt.
     */
    canMutate(fragmentId, now, limits = {}) {
        const maxPerParent = limits.maxPerParent ?? DEFAULT_MAX_MUTATIONS_PER_PARENT;
        const cooldownMs = limits.cooldownMs ?? DEFAULT_MUTATION_COOLDOWN_MS;
        const m = this.mutations.get(fragmentId);
        if (!m)
            return true;
        if (m.count >= maxPerParent)
            return false;
        return now - m.lastAt >= cooldownMs;
    }
    /**
     * Bound the pool. Never removes a live fragment. Removes, in order, dead
     * fragments (weakest first), then — only if still over — the weakest non-live
     * DERIVED fragments (generation > 0). Authored seeds that are still alive are
     * never pruned. Returns the removed ids.
     */
    prunePool(maxSize) {
        const excess = this.pool.size - Math.max(0, maxSize);
        if (excess <= 0)
            return [];
        const removable = [...this.pool.values()].filter((f) => !this.liveIds.has(f.id) && (isDead(f) || f.generation > 0));
        removable.sort((a, b) => {
            const da = isDead(a) ? 0 : 1;
            const db = isDead(b) ? 0 : 1;
            if (da !== db)
                return da - db;
            if (a.vitality !== b.vitality)
                return a.vitality - b.vitality;
            return a.createdAt - b.createdAt;
        });
        const removed = [];
        for (const f of removable.slice(0, excess)) {
            this.pool.delete(f.id);
            this.mutations.delete(f.id);
            removed.push(f.id);
        }
        return removed;
    }
    /**
     * Produce mutations for eligible live fragments using the given adapter. Each
     * mutation appends a child to the pool (lineage preserved). Returns the new
     * children. Async because the adapter may call an LLM.
     *
     * A parent is mutated at most `maxPerParent` times per session and not more
     * often than every `cooldownMs`. An attempt that throws still counts, so a
     * failing adapter cannot be hammered at the pass rate.
     */
    async mutateEligible(adapter, opts = {}) {
        const now = opts.now ?? Date.now();
        const max = opts.max ?? 3;
        const eligible = this.live().filter((f) => shouldMutate(f) && this.canMutate(f.id, now, opts));
        const children = [];
        for (const f of eligible.slice(0, max)) {
            const prev = this.mutations.get(f.id);
            this.mutations.set(f.id, { count: (prev?.count ?? 0) + 1, lastAt: now });
            const op = selectOperator(f, this.regime, this.rng);
            const dwellContext = dominantTactics(this.session).slice(0, 2);
            const result = await adapter.mutate({ source: f, op, dwellContext, lang: f.lang });
            const child = childFragment(f, result.text, op, this.session.id, now);
            this.pool.set(child.id, child);
            children.push(child);
        }
        return children;
    }
    /** Convenience: state label for a live fragment. */
    stateOf(fragmentId) {
        const f = this.pool.get(fragmentId);
        return f ? fragmentState(f) : null;
    }
}
//# sourceMappingURL=engine.js.map